import { useEffect, useMemo, useRef, useState } from "react";
import "./CreateGroupModal.scss";

const NAME_CHECK_DEBOUNCE_MS = 300;

type CreateGroupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  availableStudents: Array<{ id: string; name: string }>;
  studentsInCustomGroups: string[];
  existingGroupNames: string[];
  onCreate: (groupName: string, selectedStudents: string[]) => void;
  isLoading?: boolean;
};

const CreateGroupModal = ({
  isOpen,
  onClose,
  availableStudents,
  studentsInCustomGroups,
  existingGroupNames,
  onCreate,
  isLoading = false,
}: CreateGroupModalProps) => {
  const [groupName, setGroupName] = useState<string>("");
  const [checkedStudents, setCheckedStudents] = useState<string[]>([]);
  const [nameError, setNameError] = useState<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Normalize once per list change instead of on every keystroke
  const existingNameSet = useMemo(
    () => new Set(existingGroupNames.map((name) => name.trim().toLowerCase())),
    [existingGroupNames]
  );

  const isDuplicateName = (name: string) => existingNameSet.has(name.trim().toLowerCase());

  // Debounce the check so we don't flag the input on every keystroke while typing fast
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setNameError(isDuplicateName(groupName) ? "A group with this name already exists" : "");
    }, NAME_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [groupName, existingNameSet]);

  if (!isOpen) return null;

  const handleNameChange = (value: string) => {
    setGroupName(value);
  };

  const toggleStudent = (studentName: string) => {
    // Don't allow selection if student is already in a custom group
    if (studentsInCustomGroups.includes(studentName)) {
      return;
    }

    setCheckedStudents((prev) =>
      prev.includes(studentName)
        ? prev.filter((s) => s !== studentName)
        : [...prev, studentName]
    );
  };

  const handleCreate = () => {
    if (isDuplicateName(groupName)) {
      setNameError("A group with this name already exists");
      return;
    }
    if (groupName.trim() && checkedStudents.length > 0) {
      onCreate(groupName.trim(), checkedStudents);
      handleClose();
    }
  };

  const handleClose = () => {
    setGroupName("");
    setCheckedStudents([]);
    setNameError("");
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="create-group-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Create a group</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <line
                x1="9"
                y1="9"
                x2="27"
                y2="27"
                stroke="#9A8E8E"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <line
                x1="27"
                y1="9"
                x2="9"
                y2="27"
                stroke="#9A8E8E"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="group-name-section">
          <input
            type="text"
            value={groupName}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Please enter group name *"
            className={`group-name-input ${nameError ? "input-error" : ""}`}
          />
          {nameError && <p className="name-error">{nameError}</p>}
        </div>

        <div className="students-list">
          {availableStudents.map((student) => {
            const isDisabled = studentsInCustomGroups.includes(student.name);
            const isChecked = checkedStudents.includes(student.name);
            
            return (
              <div
                key={student.id}
                className={`student-item ${isDisabled ? 'disabled' : ''}`}
                onClick={() => toggleStudent(student.name)}
              >
                <div className="student-content">
                  <div className="checkbox">
                    {(isChecked && !isDisabled) && (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect
                          x="2"
                          y="2"
                          width="24"
                          height="24"
                          rx="4"
                          fill="#9747FF"
                        />
                        <path
                          d="M7.83 12.25L12.08 16.5L20.17 8.5"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {(!isChecked || isDisabled) && (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect
                          x="2"
                          y="2"
                          width="24"
                          height="24"
                          rx="4"
                          fill={isDisabled ? "#f0f0f0" : "white"}
                          stroke={isDisabled ? "#ccc" : "#E5E1E1"}
                          strokeWidth="2"
                        />
                      </svg>
                    )}
                  </div>
                  <span className={`student-name ${isDisabled ? 'disabled' : ''}`}>
                    {student.name}
                    {isDisabled && <span className="disabled-text"> (Already in a group)</span>}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button 
            className="create-btn"
            onClick={handleCreate}
            disabled={!groupName.trim() || checkedStudents.length === 0 || isLoading || !!nameError}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="22" viewBox="0 0 29 22" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M22.4427 0.715068C23.3963 -0.238281 25.0312 -0.238431 25.9847 0.715068L27.9593 2.69065C28.9128 3.64423 28.9128 5.27907 27.9593 6.23265L15.1517 18.9807C15.0717 19.0963 14.9839 19.208 14.8822 19.3098L12.9066 21.2854C11.953 22.2387 10.318 22.2389 9.36457 21.2854L0.715159 12.5666C-0.238386 11.6131 -0.238386 9.97821 0.715159 9.02464L2.68977 7.05003C3.64331 6.09645 5.27821 6.09645 6.23176 7.05003L11.1243 11.9807L22.4427 0.715068Z" fill="white"/>
</svg>
            {isLoading ? "Creating..." : "Create group"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;